import React, { useState, useEffect } from 'react';
import { useAuth } from '../../Auth/AuthContext';
import { ProfileService } from '../service/Profile.service';
import { ProfileFormData, User } from '../dto/Profile.dto';
import { ApiService } from '../../../shared/service/Apiservice';
import axios from 'axios';
import './styles/UserProfile.css';

const UserProfile = () => {
  const { updateUser, isAuthenticated } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ProfileFormData>({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  

  // Create API service instances
  const apiService = React.useMemo(() => new ApiService(axios.create()), []);
  const profileService = React.useMemo(() => new ProfileService(apiService), [apiService]);

  // Helper function to format address safely
  const formatAddress = (address: any): string => {
    if (!address) return '';
    
    if (typeof address === 'string') return address;
    
    const parts = [
      address.street,
      address.city,
      address.state,
      address.postalCode,
      address.country
    ].filter(part => part && part.trim() !== '');
    
    return parts.join(', ');
  };

  // Fetch user data
  const fetchUserData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const profileResponse = await profileService.getUserProfile();

      if (profileResponse.success && profileResponse.user) {
        const userData = profileResponse.user;
        setUser(userData);
        
        // Update form data with fetched user data
        setFormData({
          name: userData.name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          address: formatAddress(userData.address) || ''
        });
      } else {
        throw new Error(profileResponse.message || 'Failed to fetch profile');
      }
    } catch (err) {
      setError('Failed to fetch user data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserData();
    }
  }, [isAuthenticated]);

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsUpdating(true);
    
    try {
      const [street, ...rest] = formData.address.split(',').map(s => s.trim());
      const city = rest[0] || '';
      const state = rest[1]?.split(' ')[0] || '';
      const postalCode = rest[1]?.split(' ').slice(1).join(' ') || '';
      
      const updatePayload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: {
          street,
          city,
          state,
          postalCode
        }
      };
      
      const response = await profileService.updateUserProfile(updatePayload);
      
      if (response.success) {
        const updatedUser = response.user || { ...user!, ...updatePayload, id: user!.id };
        setUser(updatedUser);
        
        // Update auth context
        updateUser({
          name: updatedUser.name,
          email: updatedUser.email,
          phone: updatedUser.phone,
          address: formatAddress(updatedUser.address)
        });
        
        setIsEditing(false);
        setError(null);
      } else {
        throw new Error(response.message || response.error || 'Failed to update profile');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update profile';
      setError(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="account-container">
        <div className="account-card">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="account-container">
        <div className="account-card">
          <p className="error-message">No user data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="account-container">
      <div className="account-card">
        {/* Account Details Header */}
        <h2 className="account-header">
          Account Details
          {!isEditing && (
            <button
              className="edit-btn"
              onClick={() => setIsEditing(true)}
              type="button"
              style={{ marginLeft: 'auto', fontSize: '15px', padding: '6px 12px' }}
            >
              Edit
            </button>
          )}
        </h2>

        {/* Form Fields */}
        <form onSubmit={handleSubmit}>
          <div className="form-content">
            {/* First Row - Full Name */}
            <div className="form-row">
              <div className="input-group">
                <label className="input-label">
                  Full Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="input-field-name"
                    required
                  />
                ) : (
                  <div className="input-field" style={{ backgroundColor: '#f9fafb' }}>
                    {formData.name || user.name || ''}
                  </div>
                )}
              </div>
            </div>

            {/* Second Row - Email Address and Phone Number */}
            <div className="form-row">
              <div className="input-group">
                <label className="input-label">
                  Email Address
                </label>
                {isEditing ? (
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="input-field"
                  />
                ) : (
                  <div className="input-field" style={{ backgroundColor: '#f9fafb' }}>
                    {user.email}
                  </div>
                )}
              </div>
              <div className="input-group">
                <label className="input-label">
                  Phone Number
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="+91 9876543210"
                  />
                ) : (
                  <div className="input-field" style={{ backgroundColor: '#f9fafb' }}>
                    {user.phone || "Not provided"}
                  </div>
                )}
              </div>
            </div>

            {/* Third Row - Address */}
            <div className="form-row">
              <div className="input-group">
                <label className="input-label">
                  Address
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="123 Main St, City, State, ZIP"
                  />
                ) : (
                  <div className="input-field" style={{ backgroundColor: '#f9fafb' }}>
                    {formatAddress(user.address) || "Not provided"}
                  </div>
                )}
              </div>
              <div className="input-group">
                <label className="input-label">
                  Build Coins Balance
                </label>
                <div className="input-field" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', color: '#856404' }}>
                  {Math.round((user.referralCredits || 0) * 100) / 100} Build Coins
                </div>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div style={{ padding: '10px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px', marginTop: '15px' }}>
              <p style={{ color: '#d32f2f', margin: 0, fontSize: '14px' }}>{error}</p>
            </div>
          )}

          {/* Update Button */}
          <div className="button-container">
            {isEditing ? (
              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  type="submit"
                  className="update-button"
                  disabled={isUpdating}
                >
                  {isUpdating ? 'Updating...' : 'Update'}
                </button>
                <button 
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="cancel-button"
                  style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '0.375rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            ) : null}
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserProfile;